'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@miso/design-system';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import { finishCounsel } from '../model/finishCounsel';
import { fetchNextPlannerCounselAfter } from '@/features/visit-counsel/counsel-completion';
import { CounselLoadingPlaceholder } from '@/features/visit-counsel/counsel-page-layout';

export interface CounselCompleteScreenProps {
  visitCounselId: string;
  /** stage='done' 도달 시점의 counsel 스냅샷 (`completedAt` 이 비어있어도 무방) */
  counsel: VisitCounselView;
  plannerId: string;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  /** counsel 캐시 갱신 — markSessionReviewed/completeVisitCounsel 후 호출 */
  refetch: () => Promise<{ counsel?: VisitCounselView | null } | undefined>;
  /** "다음 일정 확인하기" CTA — 다음 상담이 있을 때만 호출됨 */
  onNavigateToNextCounsel: (visitCounselId: string) => void;
  /** "내 일정으로 돌아가기" CTA — 다음 상담이 없을 때 호출 */
  onNavigateToSchedule: () => void;
}

/**
 * 상담 진행 마지막 sub-stage(`customer-requests`) 직후 모든 완료 조건이 충족된 경우 표시.
 *
 * 기존 `CounselCompletionCelebration` (MUI Dialog) 의 워딩/CTA 를 그대로 옮긴 풀스크린 화면.
 * 설계 결정:
 *   1. `counsel.completedAt` 이 아직 비어있으면 마운트 시점에 `markSessionReviewed`
 *      → `completeVisitCounsel` 을 순차 호출해 종료 시각을 기록한다 (기존
 *      `CounselReviewModal` 의 "상담 종료" CTA 와 동일한 API 시퀀스).
 *   2. 종료 처리 후 `fetchNextPlannerCounselAfter` 로 다음 상담 1건을 조회해
 *      CTA 라벨/동작을 분기한다 — 다음 상담 존재 시 "다음 일정 확인하기",
 *      없으면 "내 일정으로 돌아가기".
 */
export function CounselCompleteScreen({
  visitCounselId,
  counsel,
  plannerId,
  showToast,
  refetch,
  onNavigateToNextCounsel,
  onNavigateToSchedule,
}: CounselCompleteScreenProps) {
  const [phase, setPhase] = useState<'preparing' | 'ready'>(
    counsel.completedAt ? 'ready' : 'preparing'
  );
  const [nextCounselId, setNextCounselId] = useState<string | null>(null);

  /** StrictMode 더블 마운트 / re-render 로 종료 API 가 두 번 불리지 않도록 가드 */
  const startedRef = useRef(false);
  /**
   * 실제 unmount 시점만 추적 — effect 재실행(deps 변경)으로 인한 cleanup 으로는 true 가 되지
   * 않게 한다. (이전 구현은 `counsel` 을 deps 에 두어 내부 `refetch()` 가 counsel 을 갱신
   * → effect 재실행 cleanup → cancelled=true → 진행 중이던 async 가 setPhase('ready') 전에
   * 종료되어 스켈레톤이 영구 표시되는 회귀가 있었다.)
   */
  const unmountedRef = useRef(false);
  useEffect(() => {
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    (async () => {
      try {
        let latest: VisitCounselView = counsel;

        if (!latest.completedAt) {
          // 이미 반영된 단계(409)는 건너뛰고 다음 단계로 진행하는 멱등 시퀀스.
          await finishCounsel(visitCounselId);
          // 페이지의 useMyVisitCounselDetail 캐시도 같은 값으로 동기화.
          const refetched = await refetch();
          latest = refetched?.counsel ?? latest;
        }

        let resolvedNextId: string | null = null;
        if (plannerId) {
          try {
            const next = await fetchNextPlannerCounselAfter(latest, plannerId);
            resolvedNextId = next?.id ?? null;
          } catch {
            resolvedNextId = null;
          }
        }

        if (unmountedRef.current) return;
        setNextCounselId(resolvedNextId);
        setPhase('ready');
      } catch (err) {
        if (unmountedRef.current) return;
        showToast(err instanceof Error ? err.message : '상담 종료 처리에 실패했습니다.', 'error');
        // 종료 API 실패 시 화면을 보여주지 않고 곧바로 일정으로 돌아간다.
        onNavigateToSchedule();
      }
    })();
    // startedRef 가 1회 실행을 보장하므로 deps 변경에 의한 재호출은 영향이 없다.
    // ESLint 의 exhaustive-deps 경고는 의도된 단발 실행을 표현하기 위해 비활성화.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (phase === 'preparing') {
    return <CounselLoadingPlaceholder />;
  }

  return (
    <div className="flex flex-col w-full h-full min-h-0 bg-white">
      <div className="flex-1 flex items-center justify-center px-9" role="main">
        {/*
         * Pattern B (CoverLayout 기반): title (32px semibold) 위에 보조 설명을 두고,
         * 축하 이모지를 보조 설명과 인라인으로 배치. 기존 Celebration 의 🎉 + 본문
         * "상담이 완료되었어요!" + 보조 "수고하셨습니다." 를 그대로 옮긴다.
         */}
        <div className="flex flex-col items-center gap-4 text-center">
          <span
            className="text-[48px] leading-none flex-shrink-0"
            style={{ fontFamily: 'Tossface, system-ui, sans-serif' }}
            role="img"
            aria-label="축하"
          >
            🎉
          </span>
          <h1 className="text-[32px] font-semibold text-black leading-tight tracking-[-0.02em]">
            상담이 완료되었어요!
          </h1>
          <p className="text-[18px] font-medium text-[#404040] leading-tight tracking-[-0.02em]">
            수고하셨습니다.
          </p>
        </div>
      </div>

      {/*
       * 하단 sticky CTA — StepFooterCta 와 동일한 패딩/세이프에어리어를 따라간다.
       * design-system 의 Button(primary, fixed width) 으로 시각 토큰 통일.
       */}
      <div className="w-full bg-white pt-4 px-4 pb-[calc(env(safe-area-inset-bottom)+16px)]">
        <Button
          variant="primary"
          size="l"
          shape="rectangle"
          width="fixed"
          onPress={() =>
            nextCounselId ? onNavigateToNextCounsel(nextCounselId) : onNavigateToSchedule()
          }
        >
          {nextCounselId ? '다음 일정 확인하기' : '내 일정으로 돌아가기'}
        </Button>
      </div>
    </div>
  );
}
